import { Expose } from 'class-transformer';
import { Point } from 'geojson';

import { SnModel } from '@lib/common/models';

import {
  FsLprRawFleetReadCapture,
  FsLprRawFleetReadCoordinates,
  FsLprRawFleetReadOwner,
  FsLprRawFleetReadPlateAttributes,
  FsLprRawFleetReadVehicleAttribute,
} from '../interfaces';
import { FsLprFleetRead } from './lpr-fleet-read';

// This model class represents the raw data format from the Fleet LPR API
export class FsLprRawFleetRead extends SnModel {
  @Expose()
  public id: string;

  @Expose()
  public recordType: string;

  @Expose()
  public plateNumber: string;

  @Expose()
  public contextImageId: string;

  @Expose()
  public plateImageId: string;

  @Expose()
  public imageCoordinates: FsLprRawFleetReadCoordinates;

  @Expose()
  public plateBoundingBox: FsLprRawFleetReadCoordinates;

  @Expose()
  public captures: FsLprRawFleetReadCapture[];

  @Expose()
  public owner: FsLprRawFleetReadOwner;

  @Expose()
  public vehicleName: string;

  @Expose()
  public hotlist: any;

  @Expose()
  public plateAttributes: FsLprRawFleetReadPlateAttributes;

  @Expose()
  public vehicleAttributes: FsLprRawFleetReadVehicleAttribute[];

  @Expose()
  public officerAction: string;

  @Expose()
  public hitResolution: string;

  constructor(data: any = {}) {
    super();
    this.fromJSON(data);
  }

  /**
   * Converts this raw LPR read to the standard FsLprFleetRead format
   * @returns A new FsLprFleetRead instance
   */
  public toFsLprFleetRead(): FsLprFleetRead {
    // Get the first capture for timestamp and location
    const firstCapture = this.captures && this.captures.length > 0 ? this.captures[0] : null;

    // Get the first vehicle attribute for vehicle properties
    const firstVehicleAttr =
      this.vehicleAttributes && this.vehicleAttributes.length > 0
        ? this.vehicleAttributes[0]
        : null;

    // Create the geometry point from the first capture's location
    const geometry: Point = firstCapture
      ? {
          type: 'Point',
          coordinates: [firstCapture.geoLocation.lon, firstCapture.geoLocation.lat],
        }
      : null;

    // Create the vehicle properties object
    const vehicleProperties = firstVehicleAttr
      ? {
          color: {
            name: firstVehicleAttr.color.value,
            confidence: firstVehicleAttr.color.confidenceScore,
          },
          make: {
            name: firstVehicleAttr.make.value,
            confidence: firstVehicleAttr.make.confidenceScore,
          },
          body: {
            name: firstVehicleAttr.type.value,
            confidence: firstVehicleAttr.type.confidenceScore,
          },
        }
      : null;

    // Create the FsLprFleetRead instance
    const fleetRead = new FsLprFleetRead({
      id: this.id,
      name: this.vehicleName || '',
      owner: {
        firstName: this.owner?.firstName || '',
        lastName: this.owner?.lastName || '',
        id: this.owner?.id || '',
        badgeNumber: '',
      },
      plate: this.plateNumber || '',
      plateImageUrl: this.plateImageId || '',
      contextImageUrl: this.contextImageId || '',
      vehicleProperties: vehicleProperties,
      type: this.recordType || '',
      isHit: this.hitResolution !== 'Not Set',
      hitReason: this.hitResolution !== 'Not Set' ? this.hitResolution : null,
      geometry: geometry,
      state: this.plateAttributes?.state?.value || '',
      createdTimestamp: firstCapture ? new Date(firstCapture.dateTime) : new Date(),
      imageCoordinates: this.imageCoordinates,
      plateBoundingBox: this.plateBoundingBox,
    });

    return fleetRead;
  }
}
